<template>
  <div style="padding: 0 16px">
    <a-skeleton :loading="spinning" active avatar>
      <a-table :columns="columns" :pagination="pagination" :data-source="records" size="small" row-key="index" @change="handleTableChange">
        <span slot="number" slot-scope="record">{{ records.length + 1 - record.index }}</span>
        <span slot="operator" slot-scope="operator">{{ operator ? operator.nickname : '系统' }}</span>
        <span slot="operation_alias" slot-scope="text, record">
          <span v-if="record.operation_alias === '用例执行'">
            <span>
              [
              <a>TC-{{ record.value_diff.find(item => item.value === 'case_id').before }}</a>
              ]
              <br />
              {{ record.operation_alias }}
            </span>
          </span>
          <span v-else>{{ record.operation_alias }}</span>
        </span>
        <span slot="diffItem" slot-scope="record">
          <div v-for="(item, key) in record.value_diff" :key="key">
            <span v-if="item.label !== '用例ID'">{{ item.label }}</span>
          </div>
        </span>
        <span slot="before" slot-scope="record">
          <span>
            <span v-for="(item, key) in record.value_diff" :key="key">
              <div v-if="item.value === 'name'">{{ item.before || '--' }}</div>
              <div v-else-if="item.value === 'status'">
                <a-tag v-if="item.before !== null && testPlanStatus[item.before]" :color="testPlanStatus[item.before].color">{{ testPlanStatus[item.before].label }}</a-tag>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'handlers'">
                <user-profiles v-if="item.before && item.before.length > 0" :value="item.before" avatar />
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'start_time'">{{ item.before || '--' }}</div>
              <div v-else-if="item.value === 'end_time'">{{ item.before || '--' }}</div>
              <div v-else-if="item.value === 'description'">
                <a v-if="item.before !== null" @click="onPreview(item.before, '「变更前」描述')">查看详情</a>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'type'">
                <a-badge v-if="item.before !== null" :color="testPlanTypes[item.before].color" :text="testPlanTypes[item.before].label" />
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'result'">
                <div v-if="item.before !== null && testCaseResults[item.before]">
                  <a-tag v-if="item.before !== 5" :color="testCaseResults[item.before].color">{{ testCaseResults[item.before].label }}</a-tag>
                  <a-tag v-else :color="testCaseResults[item.before].color" style="color: gray; width: 40px">{{ testCaseResults[item.before].label }}</a-tag>
                </div>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'case_id'" />
              <div v-else-if="item.value === 'requirements'">
                <span v-for="(req, index) in item.before" :key="index">
                  [<a :href="storyLink(req)" target="_blank">PR-{{ req }} </a>]
                </span>
              </div>
              <div v-else>--</div>
            </span>
          </span>
        </span>
        <span slot="after" slot-scope="record">
          <span>
            <span v-for="(item, key) in record.value_diff" :key="key">
              <div v-if="item.value === 'name'">{{ item.after || '--' }}</div>
              <div v-else-if="item.value === 'status'">
                <a-tag v-if="item.after !== null && testPlanStatus[item.after]" :color="testPlanStatus[item.after].color">{{ testPlanStatus[item.after].label }}</a-tag>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'handlers'">
                <user-profiles v-if="item.after && item.after.length > 0" :value="item.after" avatar />
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'start_time'">{{ item.after || '--' }}</div>
              <div v-else-if="item.value === 'end_time'">{{ item.after || '--' }}</div>
              <div v-else-if="item.value === 'description'">
                <a v-if="item.after !== null" @click="onPreview(item.after, '「变更后」描述')">查看详情</a>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'type'">
                <a-badge v-if="item.after" :color="testPlanTypes[item.after].color" :text="testPlanTypes[item.after].label" />
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'result'">
                <div v-if="item.after !== null && testCaseResults[item.after]">
                  <a-tag v-if="item.after !== '5'" :color="testCaseResults[item.after].color">{{ testCaseResults[item.after].label }}</a-tag>
                  <a-tag v-else :color="testCaseResults[item.after].color" style="color: gray; width: 40px">{{ testCaseResults[item.after].label }}</a-tag>
                </div>
                <span v-else>--</span>
              </div>
              <div v-else-if="item.value === 'requirements'">
                <span v-for="(req, index) in item.after" :key="index">
                  [<a :href="storyLink(req)" target="_blank">PR-{{ req }} </a>]
                </span>
              </div>
              <div v-else-if="item.value === 'case_id'" />
              <div v-else>--</div>
            </span>
          </span>
        </span>
      </a-table>
    </a-skeleton>
    <preview-modal ref="previewModal" />
  </div>
</template>

<script>
import { testPlanRecord } from '@/api/project/testPlan';
import PreviewModal from '@/pages/project/components/PreviewModal';
import { testPlanTypes, testPlanStatus, testCaseResults } from '@/utils/dictionary';

const columns = [
  {
    title: '序号',
    width: '5%',
    align: 'center',
    scopedSlots: { customRender: 'number' }
  },
  {
    title: '操作人',
    width: '10%',
    dataIndex: 'operator',
    key: 'operator',
    scopedSlots: { customRender: 'operator' }
  },
  {
    title: '操作时间',
    width: '15%',
    key: 'created_time',
    dataIndex: 'created_time'
  },
  {
    title: '变更类型',
    width: '10%',
    key: 'operation_alias',
    // dataIndex: 'operation_alias'
    scopedSlots: { customRender: 'operation_alias' }
  },
  {
    title: '变更项',
    width: '10%',
    scopedSlots: { customRender: 'diffItem' }
  },
  {
    title: '变更前',
    width: '25%',
    scopedSlots: { customRender: 'before' }
  },
  {
    title: '变更后',
    width: '25%',
    scopedSlots: { customRender: 'after' }
  }
];

export default {
  name: 'TestPlanRecord',
  components: { PreviewModal },
  props: {
    planId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      columns,
      testPlanTypes,
      testPlanStatus,
      testCaseResults,
      spinning: true,
      projectId: parseInt(this.$route.params.project_id) || null,
      pagination: {
        size: 'small',
        total: 0,
        pageSize: this.$store.getters.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: total => `共 ${total} 条`
      },
      records: [],
      recordItems: {
        name: { value: 'name', label: '测试计划名称' },
        status: { value: 'status', label: '状态' },
        handlers: { value: 'handlers', label: '测试负责人' },
        description: { value: 'description', label: '描述' },
        type: { value: 'type', label: '类型' },
        start_time: { value: 'start_time', label: '开始时间' },
        end_time: { value: 'end_time', label: '结束时间' },
        result: { value: 'result', label: '测试结果' },
        case_id: { value: 'case_id', label: '用例ID' },
        requirements: { value: 'requirements', label: '关联需求' }
      },
      userData: []
    };
  },
  computed: {
    diff() {
      return data => {
        const items = [];
        const itemKeys = Object.keys(this.recordItems);
        for (const key in data) {
          if (itemKeys.includes(key)) {
            items.push({
              ...this.recordItems[key],
              ...data[key]
            });
          }
        }
        return items;
      };
    },
    storyLink() {
      return id => {
        return this.$router.resolve({
          name: 'page:project:story:detail',
          query: { storyId: id }
        }).href;
      };
    }
  },
  created() {
    if (this.planId !== null) {
      this.getTestPlanRecord();
    }
  },
  methods: {
    getTestPlanRecord() {
      testPlanRecord({
        test_plan_id: this.planId,
        project_id: this.projectId,
        page_size: this.pagination.pageSize,
        page_number: this.pagination.current
      })
        .then(res => {
          this.pagination.total = res.data.total;
          this.records = res.data.items.map((record, index) => {
            return {
              index: index + 1,
              ...record,
              value_diff: this.diff(record.value_diff)
            };
          });
          this.spinning = false;
        })
        .catch(() => {
          this.spinning = false;
        });
    },

    onPreview(content, title = null) {
      this.utils.trackClick({ name: '预览描述' });
      this.$nextTick(() => {
        this.$refs.previewModal.onOpen(content, title);
      });
    },

    handleTableChange(pagination) {
      this.pagination = pagination;
      this.getTestPlanRecord();
    }
  }
};
</script>

<style lang="less" scoped>
.diff-item {
  display: block;
}

.ql-container.ql-snow {
  border: none;
}
</style>
